/**
 * Loads lottie-web (light SVG player) for the exit-offer gift, bundled as a lazy chunk
 * (no third-party script → CSP-friendly). Resolves undefined if the chunk fails to load;
 * the hero then falls back to 🎁.
 */
import { lottieURL } from '@/services/media';

export interface LottiePlayer {
  loadAnimation(params: { container: Element; renderer: 'svg'; loop: boolean; autoplay: boolean; path?: string; animationData?: unknown }): LottieAnimation;
}

export interface LottieAnimation { play(): void; pause(): void; destroy(): void }

let pending: Promise<LottiePlayer | undefined> | undefined;

export function loadLottie(): Promise<LottiePlayer | undefined> {
  if (typeof document === 'undefined') return Promise.resolve(undefined);
  pending ??= import('lottie-web/build/player/lottie_light')
    .then((mod) => mod.default as unknown as LottiePlayer)
    .catch(() => { pending = undefined; return undefined; });
  return pending;
}

/**
 * The exit-offer gift, built ahead of time (Checkout cancel return: under the launch splash; otherwise as soon
 * as any paywall opens). Building lottie's SVG is ~250 ms of main-thread work: done in a detached container,
 * the hero only moves the ready node in and plays it.
 */
export interface ExitGift { container: HTMLDivElement; animation: LottieAnimation }
let gift: Promise<ExitGift | undefined> | undefined;

export function preloadExitGift(): Promise<ExitGift | undefined> {
  if (typeof document === 'undefined') return Promise.resolve(undefined);
  gift ??= (async () => {
    const url = lottieURL('exit-offer-gift-animation');
    if (!url) { gift = undefined; return undefined; }
    const [lottie, data] = await Promise.all([loadLottie(), fetch(url).then((r) => (r.ok ? r.json() as Promise<unknown> : undefined))]);
    if (!lottie || !data) { gift = undefined; return undefined; }
    const container = document.createElement('div');
    container.style.cssText = 'width:100%;height:100%';
    const animation = lottie.loadAnimation({ container, renderer: 'svg', loop: true, autoplay: false, animationData: data });
    return { container, animation };
  })().catch(() => { gift = undefined; return undefined; });
  return gift;
}
